import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { api, formatApiErrorDetail, formatUSD, PRICE_PER_GRAM, MIN_ORDER_GRAMS } from "../lib/api";
import { toast } from "sonner";

const DELIVERY_OPTIONS = [
  "Direct armored transit",
  "Air cargo (arranged)",
  "Vault collection",
  "In-person pickup — Monrovia",
];

const OrderQuoteModal = ({ open, onClose, products, prefill }) => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    whatsapp: "",
    delivery_preference: DELIVERY_OPTIONS[0],
    product_id: "bar-500g",
    quantity: 1,
    custom_grams: MIN_ORDER_GRAMS,
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (prefill?.custom_grams) {
      setForm((f) => ({ ...f, product_id: "custom", custom_grams: prefill.custom_grams }));
    } else if (prefill?.product) {
      setForm((f) => ({ ...f, product_id: prefill.product.product_id, quantity: 1 }));
    }
  }, [open, prefill]);

  const list = products?.length ? products : [];
  const selected = useMemo(
    () => list.find((p) => p.product_id === form.product_id),
    [list, form.product_id]
  );

  const weight =
    form.product_id === "custom"
      ? Number(form.custom_grams) || 0
      : selected
        ? selected.weight_grams * Number(form.quantity || 0)
        : 0;
  const estimate = weight * PRICE_PER_GRAM;

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Your full name is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "A valid email is required";
    if (form.phone.trim().length < 7) e.phone = "A phone number is required";
    if (weight < MIN_ORDER_GRAMS) e.weight = `Minimum wholesale order is ${MIN_ORDER_GRAMS} g ($60,000)`;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        name: form.name,
        company: form.company || undefined,
        email: form.email,
        phone: form.phone,
        whatsapp: form.whatsapp || undefined,
        delivery_preference: form.delivery_preference,
        product_id: form.product_id,
        quantity: Number(form.quantity) || 1,
        custom_grams: form.product_id === "custom" ? Number(form.custom_grams) : undefined,
        message: form.message || undefined,
      });
      toast.success(`Order request received — reference ${data.order_id}`, {
        description: `Estimated value ${formatUSD(data.estimated_value_usd)}. Our sales desk will contact you shortly.`,
      });
      onClose();
    } catch (err) {
      toast.error("Could not submit order request", {
        description: formatApiErrorDetail(err.response?.data?.detail),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none bg-[#FAF8F5] border border-[#E6E0D4] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="order-quote-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#1A1816]">Wholesale quote request</DialogTitle>
          <DialogDescription className="text-sm text-[#6E6A63] font-light">
            No payment now — our sales desk confirms pricing, assay and settlement with you directly.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="mt-4 space-y-4" data-testid="order-quote-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="oq-name" className="text-[11px] tracking-[0.18em] uppercase text-[#6E6A63]">Full name *</Label>
              <Input id="oq-name" data-testid="order-modal-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-none bg-white border-[#E6E0D4] focus-visible:ring-[#D4AF37]/50" placeholder="Amina Koroma" />
              {errors.name && <p className="text-xs text-red-700">{errors.name}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="oq-company" className="text-[11px] tracking-[0.18em] uppercase text-[#6E6A63]">Company / institution</Label>
              <Input id="oq-company" data-testid="order-modal-company-input" value={form.company} onChange={(e) => set("company", e.target.value)} className="rounded-none bg-white border-[#E6E0D4] focus-visible:ring-[#D4AF37]/50" placeholder="Optional" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="oq-email" className="text-[11px] tracking-[0.18em] uppercase text-[#6E6A63]">Email *</Label>
              <Input id="oq-email" data-testid="order-modal-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-none bg-white border-[#E6E0D4] focus-visible:ring-[#D4AF37]/50" placeholder="you@company.com" />
              {errors.email && <p className="text-xs text-red-700">{errors.email}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="oq-phone" className="text-[11px] tracking-[0.18em] uppercase text-[#6E6A63]">Phone *</Label>
              <Input id="oq-phone" data-testid="order-modal-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-none bg-white border-[#E6E0D4] focus-visible:ring-[#D4AF37]/50" placeholder="+1 555 000 0000" />
              {errors.phone && <p className="text-xs text-red-700">{errors.phone}</p>}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[11px] tracking-[0.18em] uppercase text-[#6E6A63]">Bar selection *</Label>
              <Select value={form.product_id} onValueChange={(v) => set("product_id", v)}>
                <SelectTrigger data-testid="order-modal-product-select" className="rounded-none bg-white border-[#E6E0D4] w-full">
                  <SelectValue placeholder="Select a bar" />
                </SelectTrigger>
                <SelectContent>
                  {list.map((p) => (
                    <SelectItem key={p.product_id} value={p.product_id}>
                      {p.name} — {formatUSD(p.price_usd)}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Custom weight (from calculator)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.product_id === "custom" ? (
              <div className="space-y-1.5">
                <Label htmlFor="oq-grams" className="text-[11px] tracking-[0.18em] uppercase text-[#6E6A63]">Weight (grams) *</Label>
                <Input id="oq-grams" data-testid="order-modal-weight-input" type="number" min={MIN_ORDER_GRAMS} step={500} value={form.custom_grams} onChange={(e) => set("custom_grams", e.target.value)} className="rounded-none bg-white border-[#E6E0D4] font-mono focus-visible:ring-[#D4AF37]/50" />
                {errors.weight && <p className="text-xs text-red-700">{errors.weight}</p>}
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="oq-qty" className="text-[11px] tracking-[0.18em] uppercase text-[#6E6A63]">Quantity *</Label>
                <Input id="oq-qty" data-testid="order-modal-quantity-input" type="number" min={1} max={1000} value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className="rounded-none bg-white border-[#E6E0D4] font-mono focus-visible:ring-[#D4AF37]/50" />
                {errors.weight && <p className="text-xs text-red-700">{errors.weight}</p>}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] tracking-[0.18em] uppercase text-[#6E6A63]">Delivery preference</Label>
            <Select value={form.delivery_preference} onValueChange={(v) => set("delivery_preference", v)}>
              <SelectTrigger data-testid="order-modal-delivery-select" className="rounded-none bg-white border-[#E6E0D4] w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DELIVERY_OPTIONS.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="oq-message" className="text-[11px] tracking-[0.18em] uppercase text-[#6E6A63]">Message</Label>
            <Textarea id="oq-message" data-testid="order-modal-message-input" value={form.message} onChange={(e) => set("message", e.target.value)} className="rounded-none bg-white border-[#E6E0D4] min-h-[80px] focus-visible:ring-[#D4AF37]/50" placeholder="Settlement timeline, vault destination, questions…" />
          </div>

          <div className="bg-[#F3EFE6] border border-[#E6E0D4] p-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#6E6A63]">Estimated total</p>
              <p data-testid="order-modal-estimate" className="font-mono text-xl text-[#1A1816]">{formatUSD(estimate)}</p>
              <p className="font-mono text-[11px] text-[#6E6A63] mt-0.5">
                {weight.toLocaleString()} g · min {MIN_ORDER_GRAMS} g · from $60,000
              </p>
            </div>
          </div>

          <Button
            data-testid="order-modal-submit-btn"
            type="submit"
            disabled={submitting}
            className="w-full rounded-none h-12 bg-[#141312] text-[#FAF8F5] hover:bg-[#8C6D27] text-[12px] tracking-[0.16em] uppercase transition-colors duration-500"
          >
            {submitting ? "Submitting…" : "Submit order request"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OrderQuoteModal;
